import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CircleCheck as CheckCircle2, Package, SlidersHorizontal, Shield, Wind, Clock, Wrench, Truck } from 'lucide-react';
interface PriceFactorItem {
  id: string;
  number: string;
  title: string;
  description: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
}
const PRICE_FACTORS: PriceFactorItem[] = [{
  id: "condition",
  number: "01",
  title: "New or Used Condition",
  description: "New equipment starts with a known condition, while used-equipment pricing should reflect the individual machine, available history, visible wear and included components.",
  icon: CheckCircle2
}, {
  id: "package-scope",
  number: "02",
  title: "What the Package Includes",
  description: "A complete system may include a bull, motion base, controller, inflatable safety arena, blower and accessories. Another listing may include fewer components. Always compare the complete package rather than the headline price alone.",
  icon: Package
}, {
  id: "controller",
  number: "03",
  title: "Controller and Control System",
  description: "Different machines can use different controllers and operating systems. Review what controller comes with the machine and whether replacement components remain available.",
  icon: SlidersHorizontal
}, {
  id: "arena",
  number: "04",
  title: "Inflatable Safety Arena",
  description: "The size, condition and inclusion of the inflatable safety arena can affect the value of a complete package. Used inflatables should also be evaluated separately from the mechanical equipment.",
  icon: Shield
}, {
  id: "accessories",
  number: "05",
  title: "Blower and Accessories",
  description: "Confirm whether a blower, cables, remotes, covers or other required accessories are included with the advertised price.",
  icon: Wind
}, {
  id: "history",
  number: "06",
  title: "Condition and History",
  description: "For used machines, known repairs, previous use, visible wear and available service information can influence how buyers evaluate the price.",
  icon: Clock
}, {
  id: "parts",
  number: "07",
  title: "Parts Availability",
  description: "An older machine with difficult-to-source components may carry different long-term ownership considerations from equipment with readily available replacement parts.",
  icon: Wrench
}, {
  id: "delivery",
  number: "08",
  title: "Shipping and Delivery",
  description: "Large equipment may require freight-style delivery. Packed dimensions, weight, destination and unloading access can affect the delivery arrangement.",
  icon: Truck
}];
export default function PriceFactors() {
  return <section id="what-determines-the-price" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        {/* Header Block */}
        <div className="border-b border-border pb-8 mb-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div className="max-w-3xl">
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-tight text-foreground">
                What Determines the Price of a Mechanical Bull?
              </h2>
              <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
                Two mechanical bulls can have very different prices even when they appear similar in photographs. The difference often comes from condition, included equipment, controller setup, safety arena, accessories, support and delivery requirements.
              </p>
            </div>

            <div className="shrink-0">
              <Button asChild variant="outline" className="font-serif uppercase tracking-wider text-sm rounded-none border-foreground hover:bg-foreground hover:text-background transition-colors">
                <Link to="/ContactPage">Contact Equipment Desk</Link>
              </Button>
            </div>
          </div>
        </div>

        {/* 8 Price Factors Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {PRICE_FACTORS.map((factor, index) => {
          const IconComponent = factor.icon;
          return <Card key={factor.id} data-index={index} className="rounded-none border border-border bg-card transition-colors hover:border-foreground flex flex-col justify-between py-0 gap-0">
                <div>
                  {/* Top Plate Bar */}
                  <div className="border-b border-border bg-muted/40 p-4 flex items-center justify-between">
                    <span className="font-mono text-xs font-semibold text-primary">
                      {factor.number}
                    </span>
                    <IconComponent className="h-4 w-4 text-primary" />
                  </div>

                  <CardHeader className="p-5 pb-3">
                    <CardTitle className="font-serif text-xl font-bold uppercase text-foreground leading-tight">
                      {factor.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="p-5 pt-0">
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {factor.description}
                    </p>
                  </CardContent>
                </div>
              </Card>;
        })}
        </div>
      </div>
    </section>;
}